import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {User, UserRole} from '../types/User';
import usersService from '../services/usersService';
import {handleError} from '../../../common/utils/errorHandling';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton, AdminForm, AdminFormSection, AdminInputDropdown, AdminInputText} from "../../../layout/admin";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const UserForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: UserRole.USER as string
  });

  const {id} = useParams<{ id: string }>();
  const {t} = useTranslation();
  const [user, setUser] = useState<User | undefined>();
  const navigate = useNavigate();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  useEffect(() => {
    if (id) {
      fetchUser(id);
    }
  }, [id]);

  const fetchUser = async (userId: string): Promise<void> => {
    await handleError(
      async () => {
        const userData = await usersService.getUser(userId);
        setUser(userData);
        setFormData({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          role: userData.role as string
        });
      },
      t('admin.users.errors.fetchingUser')
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!user) {
      return;
    }

    clearErrors();
    try {
      const updatedUser: User = {
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role as UserRole
      };
      await usersService.updateUser(updatedUser);
      navigate("/users");
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const roleOptions = [
    {label: t('admin.users.roles.user'), value: UserRole.USER},
    {label: t('admin.users.roles.redactor'), value: UserRole.REDACTOR},
    {label: t('admin.users.roles.admin'), value: UserRole.ADMIN}
  ];

  return (
    <>
      <AdminFormHeader
        title={user ? `${t('admin.users.title.edit')}: ${user.firstName} ${user.lastName}` : t('admin.users.title.edit')}
        subtitle={t('admin.users.subtitle.edit')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.users.sections.userDetails')}>
              <AdminInputText
                id="firstName"
                name="firstName"
                label={t('admin.users.fields.firstName')}
                value={formData.firstName}
                onChange={handleInputChange}
                required
                error={errors.firstName}
              />
              <AdminInputText
                id="lastName"
                name="lastName"
                label={t('admin.users.fields.lastName')}
                value={formData.lastName}
                onChange={handleInputChange}
                required
                error={errors.lastName}
              />
              <AdminInputText
                id="email"
                name="email"
                label={t('admin.users.fields.email')}
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                error={errors.email}
              />
              <AdminInputDropdown
                id="role"
                name="role"
                label={t('admin.users.fields.role')}
                value={formData.role}
                options={roleOptions}
                onChange={handleInputChange}
                required
                error={errors.role}
              />
            </AdminFormSection>
          </>
        }
        rightColumn={
          <>
            {user && (
              <AdminFormSection title={t('admin.users.sections.accountStatus')}>
                <div className="mb-3">
                  <label className="form-label fw-bold">{t('admin.users.fields.confirmed')}</label>
                  <div>
                    {user.confirmed ? (
                      <span
                        className="px-3 py-1 bg-gray-600 text-gray-100 text-xs rounded-sm">{t('admin.users.status.yes')}</span>
                    ) : (
                      <span
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-sm">{t('admin.users.status.no')}</span>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">{t('admin.users.fields.disabled')}</label>
                  <div>
                    {user.disabled ? (
                      <span
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-sm">{t('admin.users.status.yes')}</span>
                    ) : (
                      <span
                        className="px-3 py-1 bg-gray-600 text-gray-100 text-xs rounded-sm">{t('admin.users.status.no')}</span>
                    )}
                  </div>
                </div>
              </AdminFormSection>
            )}
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/users")}
            />
            <AdminButton
              type="submit"
              label={t('admin.common.actions.update')}
              variant="black"
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default UserForm;

